import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
  notification,
} from "antd";
import type { RadioChangeEvent, SelectProps, UploadFile } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CAMPAIGN_TEMPLATES_OPTIONS,
  CAMPAIGN_TYPE_OPTIONS,
  ChannelKeyMap,
  RfmPeriod,
} from "../constants/labels";
import {
  TemplatesTypeEnum,
  CampaignTypeEnum,
  ChannelEnum,
  RFMPeriod,
  StatusEnum,
  TypeEnum,
  ModeEnum,
} from "../constants/enums";
import { brandMemberSegmentService } from "../services/brandMemberSegmentService";
import { useBrandStore } from "../store/brandStore";
import { brandRFMProfileService } from "../services/brandRFMProfileService";
import {
  CheckCircleFilled,
  CloseOutlined,
  DeleteFilled,
  DeleteOutlined,
  ExclamationCircleFilled,
  EyeOutlined,
  InfoCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import { VARS } from "../constants/variables";
import { RoutePaths } from "../routes/route";
import { UploadChangeParam } from "antd/es/upload";
import { Image as AntdImage } from "antd";
import DOMPurify from "dompurify";
import { htmlToWhatsappText, mapToEVouchers } from "../utils/convert";
import VoucherTable from "../components/common/VoucherTable";
import {
  CampaignDetails,
  EstimatedTotalMembersRequest,
  CampaignInfo,
} from "../models/campaign";
import { campaignService } from "../services/campaignService";
import { PostalDistricts } from "../models/postal";
import { DateRange } from "../models/time";
import {
  applyPastTimeToday,
  BlockRange,
  buildDisabledTime,
  parseBlockedRanges,
} from "../utils/date";
import { useQueryNavigator } from "../utils/navigate";
import { Voucher } from "../models/voucher";
import { EVoucher } from "../models/evoucher";
import { CampaignFormValues } from "../models/formValues";
import { helperService } from "../services/helperService";
import EditorQuill from "../components/common/EditorQuill";
import RFMGeneratingModal from "../components/common/GeneratingDataModal";
import { getSmsCharCount, getSmsSegments, isGsmText } from "../constants/text";
import { MessageTemplateData } from "../models/messageTemplates";
import MessageTemplatesModal from "../components/common/MessageTemplatesModal";
import { renderMessage } from "../utils/renderMessage";
import PreviewMemberModal from "../components/common/PreviewMemberModal";
import BillingModal from "../components/common/BillingModal";

const { Title, Text } = Typography;

const FieldRow: React.FC<{
  label: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}> = ({ label, required, children }) => (
  <Row gutter={16} align="top" style={{ marginBottom: 12 }}>
    <Col xs={24} md={10} lg={8}>
      <Text>
        {label} {required && <Text type="danger">*</Text>}
      </Text>
    </Col>
    <Col xs={24} md={14} lg={16}>
      {children}
    </Col>
  </Row>
);

const CreateCampaign: React.FC = () => {
  // ======================================================
  // 📱 Device Detection
  // ======================================================
  const isMobileOrTablet = window.innerWidth <= 1024;

  // ======================================================
  // 📝 Form & Navigation
  // ======================================================
  const [form] = Form.useForm<CampaignFormValues>();
  const location = useLocation();
  const { brandId, campaignInfo } = useBrandStore();
  const navigate = useNavigate();
  const navigateWithQuery = useQueryNavigator();
  const { Option } = Select;

  // ======================================================
  // 📊 UI State Management
  // ======================================================
  const [scheduleType, setScheduleType] = useState<number>(TypeEnum.Onetime);
  const [targetMemberMode, setTargetMemberMode] = useState<number>(
    ModeEnum.Refresh,
  );
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [messageTemplatesOpen, setMessageTemplatesOpen] = useState(false);
  const [selectedMessageTemplates, setSelectedMessageTemplates] =
    useState<MessageTemplateData | null>(null);

  const [firstReloadMember, setFirstReloadMember] = useState(true);
  const [changeRFM, setChangeRFM] = useState(false);
  const [
    isModalWarningCreateCampaignVisible,
    setIsModalWarningCreateCampaignVisible,
  ] = useState(false);
  const [selectedCampaignChannel, setSelectedCampaignChannel] =
    useState<ChannelEnum | null>(null);
  const [link, setLink] = useState("");
  const [enableTrackingLink, setEnableTrackingLink] = useState<boolean>(true);
  const [listMessageTemplates, setListMessageTemplates] = useState<
    MessageTemplateData[]
  >([]);
  const [linkOS, setLinkOS] = useState<string | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [nameError, setNameError] = useState<string | null | undefined>(
    undefined,
  );
  const [selectedTemplate, setSelectedTemplate] = useState(
    TemplatesTypeEnum.Default,
  );
  const [inactiveDays, setInactiveDays] = useState(60);
  const [loading, setLoading] = useState(false);
  const [loadingRefreshMember, setLoadingRefreshMember] = useState(false);
  const [loadingVoucher, setLoadingVoucher] = useState(false);

  const [estimatedTotalMember, setEstimatedTotalMember] = useState(0);
  const [estimatedMember, setEstimatedMember] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const openPreview = () => setIsPreviewOpen(true);
  const closePreview = () => setIsPreviewOpen(false);

  const [newCampaignId, setNewCampaignId] = useState(0);

  // ======================================================
  // 💳 Billing Flow State
  // ======================================================
  const [isBillingModalOpen, setIsBillingModalOpen] = useState(false);
  const [isPaymentSuccessModalOpen, setIsPaymentSuccessModalOpen] =
    useState(false);
  const [isPaymentFailedModalOpen, setIsPaymentFailedModalOpen] =
    useState(false);
  const [pendingPayload, setPendingPayload] = useState<CampaignDetails | null>(
    null,
  );

  const [postalFilter, setPostalFilter] = useState<string[]>([]);
  const [alreadyReload, setAlreadyReload] = useState(false);
  const [search, setSearch] = useState<string>("");
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null);
  const [selectedVoucher, setSelectedVoucher] = useState<Voucher | null>(null);
  const [selectedEVoucher, setSelectedEVoucher] = useState<EVoucher | null>(
    null,
  );

  const [campaignDetails, setCampaignDetails] =
    useState<CampaignDetails | null>(null);
  const startOfToday = dayjs().startOf("day");
  const endOfNextMonth = dayjs().add(3, "month").endOf("month");
  const [dataPostalDistrict, setDataPostalDistrict] = useState<
    PostalDistricts[]
  >([]);
  const [listBlockDate, setListBlockDate] = useState<DateRange[]>([]);
  const [listVoucher, setListVoucher] = useState<Voucher[]>([]);
  const [listEVoucher, setListEVoucher] = useState<EVoucher[]>([]);

  const filteredVouchers = listVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(search.toLowerCase()),
  );
  const filteredVEouchers = listEVoucher.filter((v) =>
    v.Name?.toLowerCase().includes(search.toLowerCase()),
  );
  // ======================================================
  // 📂 Criteria & RFM Selections
  // ======================================================
  const criteriaProfileValue = useWatch("Profile", form);
  const periodValue = useWatch("Period", form);
  const segmentValue = useWatch("Segments", form);
  const hasCriteria =
    criteriaProfileValue !== null && criteriaProfileValue !== undefined;
  const hasPeriod = !!periodValue;
  const hasSegment = Array.isArray(segmentValue)
    ? segmentValue.length > 0
    : !!segmentValue;
  // ======================================================
  // 📑 RFM Options (Segments / Profiles / Postal Codes)
  // ======================================================
  const AllBrand = "All Brands";

  const [selectedPurchaseBrand, setSelectedPurchaseBrand] = useState<string[]>([
    AllBrand,
  ]);
  const [listPurchaseBrandFilter, setListPurchaseBrandFilter] = useState<any[]>(
    [],
  );
  const [listBrandMemberSegment, setListBrandMemberSegment] = useState<
    { Id: number; Name: string }[]
  >([]);
  const [listBrandRFMProfile, setListBrandRFMProfile] = useState<
    { Id: number; Name: string; IsActive: boolean }[]
  >([]);

  const SegmentOptions: SelectProps["options"] = useMemo(
    () => listBrandMemberSegment.map((s) => ({ label: s.Name, value: s.Id })),
    [listBrandMemberSegment],
  );

  const ProfileOptions: SelectProps["options"] = useMemo(
    () =>
      [...listBrandRFMProfile].map((s) => ({
        label: s.IsActive ? `${s.Name} (Currently Selected)` : s.Name,
        value: s.Id,
      })),
    [listBrandRFMProfile],
  );

  const selectedPostalSectors = useMemo<string[]>(() => {
    if (!Array.isArray(dataPostalDistrict) || !postalFilter?.length) return [];

    const idSet = new Set(postalFilter.map((v) => String(v)));

    const merged = dataPostalDistrict
      .filter((d) => idSet.has(String(d.Id)))
      .flatMap((d) => d.PostalSectors ?? []);

    return Array.from(new Set(merged));
  }, [postalFilter, dataPostalDistrict]);

  const PostalCodeOptions: SelectProps["options"] = useMemo(
    () =>
      dataPostalDistrict.map((s) => ({
        label: `${s.PostalSectors.join(", ")} – ${s.GeneralLocation}`,
        value: s.Id,
        search: `${s.PostalSectors.join(" ")} ${s.GeneralLocation}`,
      })),
    [dataPostalDistrict],
  );

  const handleChangePurchaseBrand = (values: string[]) => {
    if (
      values.includes(AllBrand) &&
      !selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }

    if (
      !values.includes(AllBrand) &&
      selectedPurchaseBrand.includes(AllBrand)
    ) {
      setSelectedPurchaseBrand([]);
      return;
    }

    const allSelected = listPurchaseBrandFilter.every((b) =>
      values.includes(b),
    );
    if (allSelected) {
      setSelectedPurchaseBrand([AllBrand, ...listPurchaseBrandFilter]);
      return;
    }
    setSelectedPurchaseBrand(values.filter((v) => v !== AllBrand));
  };

  useEffect(() => {
    if (!listPurchaseBrandFilter?.length) return;

    if (
      selectedPurchaseBrand.includes(AllBrand) &&
      selectedPurchaseBrand.length === 1
    ) {
      const updated = [AllBrand, ...listPurchaseBrandFilter];
      setSelectedPurchaseBrand(updated);
      form.setFieldsValue({ PurchaseBrand: updated });
    } else {
      form.setFieldsValue({ PurchaseBrand: selectedPurchaseBrand });
    }
  }, [selectedPurchaseBrand, listPurchaseBrandFilter]);

  useEffect(() => {
    if (selectedTemplate === TemplatesTypeEnum.Default) {
      const allSegmentValues: number[] = (SegmentOptions ?? [])
        .map((option) => option.value)
        .filter((value): value is number => typeof value === "number");
      form.setFieldsValue({
        Period: RFMPeriod.Lifetime,
        Segments: allSegmentValues,
        Profile: listBrandRFMProfile?.find((s) => s.IsActive)?.Id,
      });
    }
  }, [selectedTemplate, ProfileOptions]);

  // ======================================================
  // 💬 Message Handling
  // ====================================================
  const msg = Form.useWatch("Message", form) ?? "";
  const isEmptyHtml = (html?: string) => {
    if (!html) return true;

    const text = html
      .replace(/<br\s*\/?>/gi, "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/gi, "")
      .trim();

    return text.length === 0;
  };
  const SHOW_REWARD_TYPES = [
    CampaignTypeEnum.Voucher,
    CampaignTypeEnum.VoucherPoint,
    CampaignTypeEnum.EVoucher,
  ];
  const campaignName = Form.useWatch("Name", form) ?? 0.0;
  const selectedType = Form.useWatch("Type", form) ?? 0.0;
  const voucherVars = ["{VoucherName}", "{VoucherExpiry}"];

  const filteredVars =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint
      ? VARS
      : VARS.filter((v) => !voucherVars.includes(v));
  const isVoucher =
    selectedType === CampaignTypeEnum.Voucher ||
    selectedType === CampaignTypeEnum.VoucherPoint;

  const actionText = isVoucher ? "Redeem Voucher" : "Order Now";

  const isFirstChange = useRef(true);
  const additionalMessageText = `\n\n${actionText}:\n${link}\n\nTo Unsubscribe:\nhttps://getz.co/a/UNSUB`;
  const additionalMessageWhatsappText = `\n\n👉 Reply with *OK to receive your membership login link*\n👉 Reply with *STOP to opt out of future WhatsApp messages*`;
  let finalMessage = msg + (enableTrackingLink ? additionalMessageText : "");
  finalMessage = finalMessage.replace(/\r\n/g, "\n");

  const charCount = getSmsCharCount(finalMessage);

  const isUnicodeText = !isGsmText(finalMessage);
  const smsSegments = getSmsSegments(finalMessage, isUnicodeText);

  // const isUnicodeMessage = isUnicode(msg);
  // const MAX_LENGTH = isUnicodeMessage ? 70 : 160;

  const insertVar = (v: string) => {
    const voucherName = selectedVoucher?.Name ?? "";
    const voucherExpiry = selectedVoucher?.EndDateFormated ?? "";

    if (v === "{VoucherName}") {
      v = voucherName;
    }

    if (v === "{VoucherExpiry}") {
      v = voucherExpiry ? dayjs(voucherExpiry).format("DD MMM YYYY") : "";
    }

    const html = form.getFieldValue("Message") || "";
    const lower = html.toLowerCase();
    const closeIdx = lower.lastIndexOf("</p>");

    let next: string;

    if (closeIdx !== -1) {
      const before = html.slice(0, closeIdx);
      const after = html.slice(closeIdx);

      const needsSpace =
        before.length > 0 && !/[ \t\n]$/.test(before.slice(-1));
      const spacer = needsSpace ? " " : "";

      next = before + spacer + v + after;
    } else {
      const needsSpace = html.length > 0 && !/[ \t\n]$/.test(html);
      next = html + (needsSpace ? " " : "") + v;
    }

    form.setFieldsValue({ Message: next });
  };

  // ======================================================
  // 📆 Campaign Details
  // ======================================================
  const getChannelCost = (channel: ChannelEnum | null): number => {
    switch (channel) {
      case ChannelEnum.WhatsAppStandard:
        return Number(process.env.REACT_APP_WHATSAPP_COST) || 0;

      case ChannelEnum.WhatsAppPremium:
        return Number(process.env.REACT_APP_WHATSAPP_PREMIUM_COST) || 0;

      case ChannelEnum.Sms:
        return Number(process.env.REACT_APP_SMS_COST) || 0;

      default:
        return 0;
    }
  };

  const GSM7_REGEX =
    /^[@£$¥èéùìòÇ\n\rØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&'()*+,\-./0-9:;<=>?A-ZÄÖÑÜ§a-zäöñüà¡¿^{}\[~\]|€]*$/;

  const isUnicode = (text: any) => !GSM7_REGEX.test(text);

  const campaignDate = Form.useWatch("CampaignDate", form);
  const cost = Form.useWatch("CostPerMember", form) ?? 0.0;
  const budget = Form.useWatch("Budget", form) ?? 0;

  // const maxReach = useMemo(
  //   () => (cost > 0 ? Math.floor(budget / cost) : 0),
  //   [budget, cost]
  // );
  //  Estimated
  const estimatedTotalCost = useMemo(() => {
    if (selectedCampaignChannel === ChannelEnum.Sms) {
      if (!budget) {
        form.setFieldsValue({
          Budget: Number(
            (estimatedTotalMember * cost * smsSegments).toFixed(2),
          ),
        });
      }
      return estimatedTotalMember * cost * smsSegments;
    }
    if (!budget) {
      form.setFieldsValue({
        Budget: Number((estimatedTotalMember * cost).toFixed(2)),
      });
    }
    return estimatedTotalMember * cost;
  }, [estimatedTotalMember, cost, selectedCampaignChannel, smsSegments]);

  const maxReach = useMemo(() => {
    const budgetFinal = budget ?? 0;

    if (selectedCampaignChannel === ChannelEnum.Sms) {
      const realCostPerMember = cost * smsSegments;
      return realCostPerMember > 0
        ? Math.floor(budgetFinal / realCostPerMember)
        : 0;
    }

    return cost > 0 ? Math.floor(budgetFinal / cost) : 0;
  }, [budget, cost, smsSegments, selectedCampaignChannel]);

  const calculateCampaignEndDate = (
    startDate: string | dayjs.Dayjs,
    memberCount: number,
    channel: ChannelEnum | null,
  ) => {
    const start = dayjs(startDate);
    let end = start;

    switch (channel) {
      case ChannelEnum.Sms: {
        const totalSeconds = Math.max(memberCount, 60);
        end = start.add(totalSeconds, "second");
        break;
      }

      case ChannelEnum.WhatsAppStandard: {
        //LIMIT AVOID BAN
        const dailyLimit = 30;
        const daysNeeded = Math.max(1, Math.ceil(memberCount / dailyLimit));
        end = start.add(daysNeeded, "day");
        break;
      }

      case ChannelEnum.WhatsAppPremium: {
        const dailyLimit = 250;
        const daysNeeded = Math.max(1, Math.ceil(memberCount / dailyLimit));
        end = start.add(daysNeeded, "day");
        break;
      }

      default: {
        end = start.add(12, "day");
        break;
      }
    }

    return end.format("YYYY-MM-DD HH:mm");
  };

  const { formattedStart, formattedEnd } = useMemo(() => {
    if (!campaignDate) {
      return { formattedStart: "", formattedEnd: "" };
    }

    return {
      formattedStart: dayjs(campaignDate).format("YYYY-MM-DD HH:mm"),
      formattedEnd: calculateCampaignEndDate(
        campaignDate,
        estimatedTotalMember,
        selectedCampaignChannel,
      ),
    };
  }, [campaignDate, estimatedTotalMember, selectedCampaignChannel]);

  // ======================================================
  // 🔄 Member Refresh Logic
  // ======================================================

  const onChangeRFM = () => {
    if (!firstReloadMember) {
      setChangeRFM(true);
    }
    setAlreadyReload(false);
  };
  const postalTotal = useMemo(
    () => PostalCodeOptions.filter((o) => o.value !== "all").length,
    [PostalCodeOptions],
  );

  const handleRefreshMember = async () => {
    setLoadingRefreshMember(true);

    try {
      if (firstReloadMember) {
        setFirstReloadMember(false);
      }
      const data: EstimatedTotalMembersRequest = {
        BrandId: brandId ?? "",
        RFMProfile: criteriaProfileValue ?? 0,
        RFMPeriod: periodValue ?? 0,
        SegmentIds: segmentValue ?? [],
        IncludeListMember: true,
        FromAge: form.getFieldValue("ageRange")?.from,
        ToAge: form.getFieldValue("ageRange")?.to,
        PostCode: selectedPostalSectors,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : selectedPurchaseBrand,
        CampaignType: selectedType,
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
      };
      const result = await campaignService.estimatedTotalMember(data);
      setEstimatedTotalMember(result.EstimatedCountMember);
      setEstimatedMember(result.ListMember);
      setChangeRFM(false);
      setAlreadyReload(true);
      return result;
    } catch (error) {
      console.error("Failed to refresh member:", error);
    } finally {
      setLoadingRefreshMember(false);
    }
  };

  // ======================================================
  //  Schedule Type Switching
  // ======================================================
  const onScheduleChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setScheduleType(val);
  };

  const onModeChange = (e: RadioChangeEvent) => {
    const val = e.target.value;
    setTargetMemberMode(val);
  };

  // ======================================================
  //  Form Submission
  // ======================================================
  const handleSubmit = async (values: CampaignFormValues) => {
    try {
      const estimated = await handleRefreshMember();
      const nextEstimatedCost =
        selectedCampaignChannel === ChannelEnum.Sms
          ? estimated.EstimatedCountMember * cost * smsSegments
          : estimated.EstimatedCountMember * cost;

      const formattedCampaignEndDate = calculateCampaignEndDate(
        formattedStart,
        estimated.EstimatedCountMember,
        selectedCampaignChannel,
      );

      const shouldAppendTrackingText =
        enableTrackingLink &&
        // (selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
        selectedCampaignChannel === ChannelEnum.Sms;

      const trackingText = shouldAppendTrackingText
        ? additionalMessageText
        : selectedCampaignChannel === ChannelEnum.WhatsAppStandard
          ? additionalMessageWhatsappText
          : "";

      if (estimated.EstimatedCountMember === 0) {
        form.setFields([
          {
            name: "TotalMembers" as any,
            errors: ["No members matched the selected criteria."],
          },
        ]);
        form.scrollToField("Name" as any, {
          behavior: "smooth",
          block: "start",
        });
        return;
      }
      const payload: CampaignDetails = {
        Name: values.Name,
        Type: values.Type,
        RFMPeriod: values.Period,
        FromAge: values.ageRange?.from,
        ToAge: values.ageRange?.to,
        Messages: [
          {
            Content:
              selectedCampaignChannel === ChannelEnum.WhatsAppStandard
                ? htmlToWhatsappText(values?.Message ?? "") + trackingText
                : selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                  ? (selectedMessageTemplates?.Content ?? "")
                  : selectedCampaignChannel === ChannelEnum.Sms
                    ? (values?.Message ?? "") + trackingText
                    : (values?.Message ?? ""),
            Link: link,
            CampaignTemplates:
              selectedCampaignChannel === ChannelEnum.WhatsAppPremium
                ? selectedMessageTemplates
                : null,
          },
        ],

        Segments: (values.Segments ?? []).map((id) => ({ SegmentId: id })),
        PostalDistricts: (values.PostalCode ?? []).map((id) => ({
          DistrictId: Number(id),
        })),
        RFMProfile: values.Profile,
        BrandId: brandId ?? undefined,
        Status: values.Status,
        VoucherId:
          selectedType === CampaignTypeEnum.VoucherPoint ||
          selectedType === CampaignTypeEnum.Voucher
            ? selectedVoucher?.Id
            : undefined,
        SKU: selectedEVoucher?.SKU ?? undefined,
        CampaignStart: formattedStart,
        CampaignEnd: formattedCampaignEndDate,
        Budget: values.Budget,
        EstimatedTotalMembers: estimated.EstimatedCountMember,
        ScheduleType: scheduleType,
        ImageUrl: previewUrl ?? undefined,
        Channel: selectedCampaignChannel ?? undefined,
        PurchaseBrand: selectedPurchaseBrand.includes(AllBrand)
          ? null
          : JSON.stringify(selectedPurchaseBrand),
        TemplateType: selectedTemplate,
        InactiveDays:
          selectedTemplate === TemplatesTypeEnum.Default ? inactiveDays : 0,
        Mode: values.Mode,
        IsBilled: 0,
      };
      setCampaignDetails(payload);

      if (payload.Status === StatusEnum.Draft) {
        // Draft — skip billing, create directly
        setLoading(true);
        try {
          const resultId = await campaignService.createCampaignDetails(payload);
          if (!resultId) {
            alert("Failed to create campaign.");
            return;
          }
          navigate(
            `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${resultId}`,
          );
        } catch {
          alert("Failed to create campaign.");
        } finally {
          setLoading(false);
        }
      } else {
        setPendingPayload(payload);
        setIsBillingModalOpen(true);
      }
    } catch (err) {
      console.error(err);
      return Promise.reject(err);
    }
  };

  // Helper
  const isChannelEnum = (v: unknown): v is ChannelEnum =>
    typeof v === "number" && v in ChannelEnum;

  // ======================================================
  // 🗂 Data Fetching
  // ======================================================
  const fetchListAccountBrand = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getListAccountBrand(brandId);
        if (data) {
          setListPurchaseBrandFilter(
            Array.from(
              new Set(
                data
                  .map((s: any) => s.BrandName?.trim())
                  .filter((name: any) => name && name !== ""),
              ),
            ).sort((a: any, b: any) => a.localeCompare(b)),
          );
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getAllVouchers();
        if (data) {
          setListVoucher(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchEVoucherList = async () => {
    if (brandId) {
      try {
        setLoadingVoucher(true);
        const data = await campaignService.getAllEVouchers(
          campaignInfo?.Email ?? "",
        );
        if (data) {
          const mapped = mapToEVouchers(data);
          setListEVoucher(mapped);
        }
      } catch (error) {
        console.error("Error fetching E-Voucher:", error);
      } finally {
        setLoadingVoucher(false);
      }
    }
  };

  const fetchBlockDate = async () => {
    if (brandId && selectedCampaignChannel) {
      try {
        const data = await campaignService.getBlockedCampaignDateRanges(
          brandId,
          selectedCampaignChannel,
        );
        if (data) {
          setListBlockDate(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };

  const fetchPostalDistrict = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllPostalDistrict();
        if (data) {
          setDataPostalDistrict(data);
        }
      } catch (error) {
        console.error("Error fetching postal district:", error);
      }
    }
  };

  const fetchBrandMemberSegment = async () => {
    if (brandId) {
      try {
        const data =
          await brandMemberSegmentService.listBrandMemberSegment(brandId);
        if (data) {
          setListBrandMemberSegment(data);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchBrandRFMProfile = async () => {
    if (brandId) {
      try {
        const data = await brandRFMProfileService.getBrandRFMProfile(brandId);
        if (data) {
          setListBrandRFMProfile(data);
          form.setFieldsValue({ Profile: data.find((s) => s.IsActive)?.Id });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
  };

  const fetchLinkOS = async () => {
    if (brandId) {
      try {
        const data = await helperService.getPrimaryDomainByBrandId(brandId);
        if (data) {
          setLinkOS(data);
        }
      } catch (error) {
        console.error("Error get link OS:", error);
      }
    }
  };

  const FetchCheckAvailableCampaignName = async (value: string) => {
    if (brandId) {
      try {
        const data = await campaignService.checkAvailableCampaignName(
          value,
          undefined,
        );
        return data;
      } catch (error) {
        console.error("Error Check Available Campaign Name:", error);
      }
    }
  };

  const fetchShortenLink = async (value: string) => {
    if (brandId) {
      try {
        const data = await helperService.getShortenLink(value);
        if (data) {
          setLink(data);
          setShowLink(true);
        }
      } catch (error) {
        console.error("Error get shorten link:", error);
      }
    }
  };

  const fetchMessageTemplates = async () => {
    if (brandId) {
      try {
        const data = await campaignService.getAllMessageTemplates();
        if (data) {
          setListMessageTemplates(data);
        }
      } catch (error) {
        console.error("Error fetch message templates:", error);
      }
    }
  };

  // ======================================================
  // ⚛️ Effects
  // ======================================================

  useEffect(() => {
    if (campaignInfo?.Email) {
      fetchEVoucherList();
    }
  }, [campaignInfo]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const urlChannel = urlParams.get("campaign_channel");

    const parsed = urlChannel ? parseInt(urlChannel, 10) : NaN;
    const finalValue = isChannelEnum(parsed) ? parsed : ChannelEnum.Sms;

    setSelectedCampaignChannel(finalValue);

    form.setFieldsValue({
      CostPerMember: getChannelCost(finalValue),
    });

    // if (urlChannel !== null) {
    //   urlParams.delete("campaign_channel");

    //   const newUrl =
    //     window.location.pathname +
    //     (urlParams.toString() ? `?${urlParams.toString()}` : "");

    //   window.history.replaceState({}, "", newUrl);
    // }
  }, [location.search]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (brandId && selectedCampaignChannel) {
        await fetchListAccountBrand();
        await fetchBrandMemberSegment();
        await fetchBrandRFMProfile();
        await fetchPostalDistrict();
        await fetchBlockDate();
        await fetchVoucherList();
        await fetchLinkOS();
        // await fetchMessageTemplates();
      }
    }, 100);
    const fetchSettings = async () => {};
    fetchSettings();

    return () => clearTimeout(timeout);
  }, [brandId, selectedCampaignChannel]);

  useEffect(() => {
    if (nameError === null) {
      form.validateFields(["Name"]);
    }
  }, [nameError]);

  useEffect(() => {
    if (SHOW_REWARD_TYPES.includes(selectedType))
      form.validateFields(["Voucher"]);
  }, [selectedVoucher, selectedEVoucher]);

  // ======================================================
  // 📤 Upload Handling
  // ======================================================
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadingFile, setUploadingFile] = useState<UploadFile | null>(null);
  const [progressUpload, setProgressUpload] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [notif, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = () => {
    notif.error({
      message: "Error!",
      description:
        "An error occurred while uploading the image. Please try again with a different image or contact the administrator.",
      placement: "top",
    });
  };

  const uploadToS3 = async (file: File) => {
    let simInterval: number | undefined;
    let finished = false;
    const start = Date.now();

    const startSimulatedProgress = () => {
      setProgressUpload(1);
      simInterval = window.setInterval(() => {
        setProgressUpload((prev) => {
          if (finished) return prev;
          if (prev >= 75) return 75;
          const next = prev + 2;
          return next >= 75 ? 75 : next;
        });
        return;
      }, 100);
    };

    const clearSim = () => {
      if (simInterval) {
        window.clearInterval(simInterval);
        simInterval = undefined;
      }
    };

    try {
      setIsUploading(true);
      setProgressUpload(0);
      startSimulatedProgress();

      const res = await campaignService.uploadCampaignImage(file);
      finished = true;
      clearSim();
      if (!res || !res.result || !res.result.Url) {
        openNotificationWithIcon();
      }
      const elapsed = Date.now() - start;
      const remain = elapsed < 1000 ? 1000 - elapsed : 0;
      if (remain > 0) {
        await new Promise((r) => setTimeout(r, remain));
      }

      setProgressUpload(100);

      const imageUrl = res?.result?.Url as string | undefined;
      if (!imageUrl) throw new Error("Upload succeeded but URL is missing.");

      form?.setFieldsValue?.({ Image: imageUrl });
      setPreviewUrl(imageUrl);

      setFileList((lst) =>
        lst.length
          ? [
              {
                ...lst[0],
                status: "done",
                url: imageUrl,
                response: { Url: imageUrl },
              },
            ]
          : lst,
      );

      message.success("Image uploaded.");
    } catch (err: any) {
      finished = true;
      clearSim();
      message.error(
        err?.response?.data?.message || err?.message || "Upload failed",
      );
      setFileList([]);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
      form?.setFieldsValue?.({ Image: undefined });
    } finally {
      setTimeout(() => setProgressUpload(0), 300);
      setIsUploading(false);
    }
  };

  const handleChangeUpload = (info: UploadChangeParam<UploadFile<any>>) => {
    const latestList = info.fileList.slice(-1);
    setFileList(latestList);
    setUploadingFile(latestList[0] ?? null);
    if (latestList.length) setProgressUpload(0);
  };

  const beforeUpload = (file: File) => {
    const isValidType = ["image/jpeg", "image/png"].includes(file.type);
    const isLt5M = file.size / 1024 / 1024 <= 5;

    if (!isValidType) {
      message.error("Only JPG/PNG are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!isLt5M) {
      message.error("Max file size is 5 MB.");
      return Upload.LIST_IGNORE;
    }

    const url = URL.createObjectURL(file);

    const item: UploadFile = {
      uid: `${Date.now()}`,
      name: file.name,
      status: "uploading",
      originFileObj: file as any,
      url,
      thumbUrl: url,
    };
    setFileList([item]);

    uploadToS3(file);

    return false;
  };

  // ======================================================
  // 🔎 Search & Voucher Selection
  // ======================================================
  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setFileList([]);
    setUploadingFile(null);
    setProgressUpload(0);
  };

  const summary = selectedVoucher
    ? `${selectedVoucher.Name} | ${selectedVoucher.DiscountLabel}`
    : selectedEVoucher
      ? `${selectedEVoucher.Name} | ${selectedEVoucher.Price}`
      : "No rewards selected";

  const templatesSummary = selectedMessageTemplates
    ? `${selectedMessageTemplates.Name}`
    : "No templates selected";

  const blocked: BlockRange[] = parseBlockedRanges(
    listBlockDate,
    "YYYY-MM-DD HH:mm",
  );
  const isEmpty = (v: any) => v === undefined || v === null || v === "";
  const handleError = ({ errorFields }: any) => {
    if (errorFields && errorFields.length > 0) {
      form.scrollToField(errorFields[0].name, {
        behavior: "smooth",
        block: "center",
      });

      message.error(errorFields[0].errors?.[0]);
    }
  };

  useEffect(() => {
    if (selectedCampaignChannel === ChannelEnum.WhatsAppPremium) {
      form.setFieldsValue({
        Message: selectedMessageTemplates?.Content ?? "",
      });
      form.validateFields(["Message"]);
    }
  }, [selectedCampaignChannel, selectedMessageTemplates, form]);

  const onPaymentlater = async () => {
    setIsBillingModalOpen(false);
    setLoading(true);
    try {
      const resultId = await campaignService.createCampaignDetails(
        pendingPayload!,
      );
      if (!resultId) {
        alert("Failed to create campaign.");
        return;
      }
      navigate(
        `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${resultId}`,
      );
    } catch {
      alert("Failed to create campaign.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      onFinishFailed={handleError}
      initialValues={{
        PurchaseBrand: selectedPurchaseBrand,
        ScheduleType: TypeEnum.Onetime,
        Segments: [-2, -3, -4],
        CostPerMember: getChannelCost(selectedCampaignChannel),
        TemplateType: TemplatesTypeEnum.Default,
        InactiveDays: inactiveDays,
        Mode: ModeEnum.Refresh,
      }}
    >
      {contextHolder}
      <Card
        title={
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={5} style={{ margin: 0 }}>
                Create{" "}
                {selectedCampaignChannel
                  ? ChannelKeyMap[selectedCampaignChannel]
                  : ""}{" "}
                Campaign
              </Title>
            </Col>
            <Col>
              <Button
                color="primary"
                variant="outlined"
                onClick={() =>
                  navigateWithQuery(
                    RoutePaths.IFRAME_TARGET_MARKETING_HOME,
                    "campaign_channel",
                    null,
                  )
                }
              >
                Back
              </Button>
            </Col>
          </Row>
        }
        className="card-form-campaign"
      >
        {/* {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
          <Alert
            className=""
            type="info"
            description={
              <span>
                Campaign delivery is processed in batches, with a limit of 1,000
                recipients per day.
              </span>
            }
            showIcon
            style={{ marginBottom: 16 }}
          />
        )} */}
        {/* Campaign Details */}
        <Title level={5}>Campaign Details</Title>
        <Divider style={{ margin: "10px 0" }} />
        <Form.Item name="Status" hidden>
          <Input />
        </Form.Item>
        <FieldRow label="Campaign Name" required>
          <Form.Item
            name="Name"
            validateStatus={nameError ? "error" : ""}
            help={nameError}
            rules={[
              { required: true, message: "Please input campaign name" },
              {
                validator: async (_, value) => {
                  if (!value) {
                    setNameError("Please input campaign name");
                  }
                  if (value && value.trim() === "") {
                    setNameError(
                      "Campaign name cannot be empty or spaces only",
                    );
                  }

                  const invalidCharRegex = /[^a-zA-Z0-9 _-]/;
                  if (invalidCharRegex.test(value)) {
                    setNameError(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                    return Promise.reject(
                      "Campaign name cannot contain special characters like & * @ # etc.",
                    );
                  }

                  if (nameError && nameError !== "") {
                    return Promise.reject(nameError);
                  }

                  return Promise.resolve();
                },
              },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Input
              placeholder="Input Campaign Name"
              onChange={async (e) => {
                const value = e.target.value.trim();

                if (value) {
                  const generatedLink = `${linkOS}?utm_source=${
                    selectedCampaignChannel
                      ? ChannelKeyMap[selectedCampaignChannel]
                      : ""
                  }&utm_campaign=${value}&utm_medium=OS`;
                  const isAvailable =
                    await FetchCheckAvailableCampaignName(value);

                  if (isAvailable) {
                    setNameError(null);

                    fetchShortenLink(generatedLink);
                  } else {
                    setNameError(
                      "A campaign with this name already exists. Please enter a unique campaign name",
                    );
                  }
                }
              }}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Campaign Type" required>
          <Form.Item
            name="Type"
            rules={[{ required: true, message: "Select Campaign Type" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="Select Campaign Type"
              options={CAMPAIGN_TYPE_OPTIONS}
              allowClear
              onChange={() => {
                setSelectedEVoucher(null);
                setSelectedVoucher(null);
                setSelectedMessageTemplates(null);
              }}
            />
          </Form.Item>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Choose the reward type that defines how revenue will be tracked.
          </div>
        </FieldRow>
        <FieldRow label="Campaign Template Type" required>
          <Form.Item
            name="TemplateType"
            rules={[
              { required: true, message: "Select Campaign Template Type" },
            ]}
            style={{ marginBottom: 0 }}
          >
            <Select
              placeholder="Select Campaign Template Type"
              options={CAMPAIGN_TEMPLATES_OPTIONS}
              value={selectedTemplate}
              onChange={(value) => {
                setSelectedTemplate(value);
                onChangeRFM();
              }}
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {selectedTemplate === TemplatesTypeEnum.Default
                ? `This template uses a pre-configured RFM segmentation targeting members with no transactions or visits in the last ${inactiveDays} days`
                : "Fully customizable RFM segmentation. Select your own period, segments and criteria."}
            </div>
          </Form.Item>
        </FieldRow>
        {selectedTemplate === TemplatesTypeEnum.Default && (
          <FieldRow label="Inactive Period (Days)">
            <Form.Item
              name="InactiveDays"
              rules={[{ required: true, message: "Please input days" }]}
              style={{ marginBottom: 0 }}
            >
              <InputNumber
                min={1}
                value={inactiveDays}
                onChange={(value) => setInactiveDays(value || 60)}
                placeholder="Inactive Days"
                addonAfter="Days"
                style={{ width: "100%" }}
              />
            </Form.Item>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Define the number of days since a members last transaction or
              visit to be considered inactive.
            </div>
          </FieldRow>
        )}
        <FieldRow label="Selected RFM Period" required>
          <Form.Item
            name="Period"
            rules={[{ required: true, message: "Please select RFM Period" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Period"
              options={RfmPeriod}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>

        <FieldRow label="Target RFM Segment" required>
          <Form.Item
            name="Segments"
            rules={[{ required: true, message: "Select Target RFM Segment" }]}
            style={{ marginBottom: 0 }}
          >
            <Select
              onChange={onChangeRFM}
              mode="multiple"
              placeholder="Select Target RFM Segment"
              options={SegmentOptions}
              allowClear
              style={{ width: "100%" }}
              maxTagCount={0}
              maxTagPlaceholder={(omittedValues) => {
                const total = SegmentOptions?.length ?? 0;
                const selected = omittedValues?.length ?? 0;

                if (selected === 0) return null;
                if (selected === total) return "All Segments";

                return `${selected}/${total} selected`;
              }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="RFM Criteria Profile" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="Profile"
            rules={[
              { required: true, message: "Please select RFM Criteria Profile" },
            ]}
          >
            <Select
              onChange={onChangeRFM}
              placeholder="Select RFM Criteria Profile"
              options={ProfileOptions}
              style={{ width: "100%" }}
              disabled={selectedTemplate === TemplatesTypeEnum.Default}
            />
          </Form.Item>
        </FieldRow>
        <FieldRow label="Target Demographics (age)">
          <Row gutter={[12, 12]}>
            {/* FROM AGE */}
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "from"]}
                style={{ marginBottom: 0 }}
                dependencies={[["ageRange", "to"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const to = getFieldValue(["ageRange", "to"]);
                      if (isEmpty(value) && isEmpty(to))
                        return Promise.resolve();
                      if (isEmpty(value) && !isEmpty(to)) {
                        return Promise.reject(
                          new Error("Please input minimum age"),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  placeholder="From Age"
                  style={{ width: "100%" }}
                  onChange={onChangeRFM}
                />
              </Form.Item>
            </Col>

            <Col span={1} style={{ textAlign: "center", lineHeight: "32px" }}>
              <Divider
                style={{
                  margin: "15px 0",
                  borderBlockStart: "1px solid black",
                }}
              />
            </Col>

            {/* TO AGE */}
            <Col xl={4} xs={10}>
              <Form.Item
                name={["ageRange", "to"]}
                style={{ marginBottom: 0 }}
                dependencies={[["ageRange", "from"]]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const from = getFieldValue(["ageRange", "from"]);
                      if (isEmpty(value) && isEmpty(from))
                        return Promise.resolve();
                      if (!isEmpty(from) && isEmpty(value)) {
                        return Promise.reject(
                          new Error("Please input maximum age"),
                        );
                      }
                      if (!isEmpty(from) && !isEmpty(value)) {
                        const a = Number(from);
                        const b = Number(value);
                        if (!(b > a)) {
                          return Promise.reject(
                            new Error("To age must be higher than From age"),
                          );
                        }
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputNumber
                  min={0}
                  step={1}
                  placeholder="To Age"
                  style={{ width: "100%" }}
                  onChange={onChangeRFM}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Specify the age range of members to include in the campaign.
            </div>
          </Row>
        </FieldRow>
        <FieldRow label="Target Geographic (Postal Code)">
          <Form.Item style={{ marginBottom: 0 }} name="PostalCode">
            <Select
              mode="multiple"
              placeholder="Select Postal Code"
              options={PostalCodeOptions}
              value={postalFilter}
              onChange={(value: any) => {
                onChangeRFM();
                setPostalFilter(value);
              }}
              style={{ width: "100%" }}
              showSearch={false}
              filterOption={false}
              maxTagCount={0}
              maxTagPlaceholder={() => {
                if (!postalFilter || postalFilter.length === 0) return null;
                if (
                  postalFilter.includes("all") ||
                  postalFilter.length === postalTotal
                ) {
                  return "All Postal Sectors";
                }
                return `${postalFilter.length}/${postalTotal} selected`;
              }}
            />
          </Form.Item>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            2 digits of 6-digit postal codes
          </div>
        </FieldRow>

        <FieldRow label="Purchase Brand" required>
          <Form.Item
            style={{ marginBottom: 0 }}
            name="PurchaseBrand"
            rules={[{ required: true, message: "Please select Brand" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select Brands"
              maxTagCount={0}
              onChange={handleChangePurchaseBrand}
              value={
                form.getFieldValue("PurchaseBrand")?.includes(AllBrand)
                  ? [AllBrand, ...listPurchaseBrandFilter]
                  : form.getFieldValue("PurchaseBrand")
              }
              maxTagPlaceholder={() => {
                const value = form.getFieldValue("PurchaseBrand") || [];
                return value.includes(AllBrand)
                  ? AllBrand
                  : `${value.length}/${listPurchaseBrandFilter.length} selected`;
              }}
            >
              <Option key={AllBrand} value={AllBrand}>
                {AllBrand}
              </Option>
              {listPurchaseBrandFilter.map((brand) => (
                <Option key={brand} value={brand}>
                  {brand}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FieldRow>
        <FieldRow
          label={
            <>
              Estimated Total Members{" "}
              {/* <DownloadOutlined onClick={handleDownloadEstimated} />{" "} */}
            </>
          }
          // label="Estimated Total Members"
        >
          <Form.Item name="TotalMembers" style={{ marginBottom: 0 }}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <InputNumber
                style={{ width: isMobileOrTablet ? "70%" : "87%" }}
                value={estimatedTotalMember}
                addonAfter="Members"
                disabled
              />
              <Button
                disabled={!(hasCriteria && hasPeriod && hasSegment)}
                color="primary"
                variant="outlined"
                loading={loadingRefreshMember}
                onClick={handleRefreshMember}
                style={{
                  width: isMobileOrTablet ? "27%" : "12%",
                  marginLeft: "auto",
                }}
              >
                {loadingRefreshMember ? (
                  "Loading ..."
                ) : (
                  <>
                    <SyncOutlined /> Reload
                  </>
                )}
              </Button>
            </div>

            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              {/* 1️⃣ RFM changed → highest priority */}
              {changeRFM && (
                <Alert
                  type="error"
                  showIcon
                  description="The displayed total member count may not be accurate. Please click Reload to ensure the latest data is reflected based on your current selections."
                />
              )}

              {/* 2️⃣ Ready but not reloaded */}
              {!changeRFM &&
                hasCriteria &&
                hasPeriod &&
                hasSegment &&
                !alreadyReload && (
                  <Alert
                    type="info"
                    showIcon
                    description="Estimation total number of members matched based on your selected RFM Period, Target Segment(s), RFM Criteria Profile, Target Demographics, and Target Geographic. Click Reload to view the result."
                  />
                )}

              {/* 3️⃣ Zero result */}
              {!changeRFM && !alreadyReload && estimatedTotalMember === 0 && (
                <div style={{ fontSize: 13, color: "#595959" }}>
                  Estimation total number of members matched based on your
                  selected RFM Period, Target Segment(s), RFM Criteria Profile,
                  Target Demographics, and Target Geographic.
                </div>
              )}

              {/* 4️⃣ Has members → Preview */}
              {!changeRFM && alreadyReload && estimatedTotalMember > 0 && (
                <Alert
                  type="info"
                  showIcon
                  icon={
                    <img
                      src="/icon/icon-memberlist.svg"
                      alt="members"
                      style={{ width: 40, height: 40 }}
                    />
                  }
                  message={
                    <Row align="middle" justify="space-between">
                      <Col>
                        <div style={{ fontSize: 20, fontWeight: 600 }}>
                          {estimatedTotalMember}
                        </div>
                        <div style={{ fontSize: 13, color: "#595959" }}>
                          Estimation total members will receive this campaign
                        </div>
                      </Col>

                      <Col>
                        <Button
                          type="primary"
                          icon={<EyeOutlined />}
                          onClick={openPreview}
                        >
                          Preview Members
                        </Button>
                      </Col>
                    </Row>
                  }
                />
              )}
            </div>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Channel Cost per Message">
          <Space.Compact style={{ width: "100%" }}>
            <Input
              disabled
              value="$"
              style={{ width: 40, textAlign: "center" }}
            />
            <Form.Item name="CostPerMember" noStyle>
              <InputNumber
                style={{ width: "100%" }}
                min={0}
                step={0.01}
                stringMode
                placeholder="0.00"
                disabled
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Cost to send one message, based on selected channel.
          </div>
        </FieldRow>

        <FieldRow label="Estimated Budget">
          <Form.Item name="EstimatedBudget" noStyle>
            <InputNumber
              style={{ width: "100%" }}
              value={
                estimatedTotalCost ? Number(estimatedTotalCost.toFixed(2)) : 0
              }
              disabled
            />
            <div style={{ color: "#8c8c8c", marginTop: 6 }}>
              Automatically calculated cost: Total Members X Channel Cost{" "}
              {selectedCampaignChannel === ChannelEnum.Sms && "X Total SMS"}
            </div>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Schedule Campaign" required>
          <Form.Item name="ScheduleType" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onScheduleChange}>
              <Space direction="vertical">
                <Radio value={1}>
                  <Text>One‑Time Campaign</Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Send this campaign once at a specific date and time you
                    choose.
                  </div>
                </Radio>
                <Radio value="recurring" disabled>
                  <Text disabled>Recurring Campaign</Text>
                  <div
                    style={{
                      color: "rgba(0,0,0,0.25)",
                      fontWeight: "normal",
                    }}
                  >
                    Send this campaign on a repeated schedule—weekly or monthly.
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>

        <FieldRow label="Target Members Mode" required>
          <Form.Item name="Mode" noStyle rules={[{ required: true }]}>
            <Radio.Group onChange={onModeChange} style={{ width: "100%" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Radio value={ModeEnum.Refresh}>
                  <Text>Refresh target before sending</Text>
                  <div style={{ color: "grey", fontWeight: "normal" }}>
                    Target members will be refreshed 1 day before send time.
                  </div>
                </Radio>
                <Alert
                  description={
                    <>
                      Target members may change before sending. This can affect
                      final reach and budget usage.
                    </>
                  }
                  type="warning"
                  showIcon
                />
                <Radio value={ModeEnum.Fixed}>
                  <Text>Use fixed target</Text>
                  <div
                    style={{
                      color: "grey",
                      fontWeight: "normal",
                    }}
                  >
                    Target members remains the same as when the campaign was
                    created.{" "}
                  </div>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
        </FieldRow>
        {scheduleType === TypeEnum.Onetime ? (
          <FieldRow label="Send Date & Time" required>
            <Form.Item
              name="CampaignDate"
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: "Select date and time" }]}
            >
              <DatePicker
                showTime={{
                  format: "HH:mm",
                  minuteStep: 15,
                  showSecond: false,
                }}
                disabledDate={(current) => {
                  if (!current) return false;

                  return (
                    current.isBefore(startOfToday, "day") ||
                    current.isAfter(endOfNextMonth, "day")
                  );
                }}
                disabledTime={(date) => {
                  if (!date) return {};
                  const base = buildDisabledTime(date, blocked);
                  const test = applyPastTimeToday(date, base);
                  return test;
                }}
                format="YYYY-MM-DD HH:mm"
                style={{ width: "100%", height: "unset !important" }}
                placeholder="Select Send Date & Time"
              />
            </Form.Item>
            {campaignDate && (
              <Alert
                description={
                  <>
                    {
                      <>
                        Your campaign will start at {formattedStart} and is
                        estimated to complete by {formattedEnd}.
                      </>
                    }
                  </>
                }
                type="info"
                showIcon
                style={{ marginTop: 10 }}
              />
            )}
          </FieldRow>
        ) : (
          <>
            <FieldRow label="Recurring Schedule" required>
              <Space wrap>
                <Form.Item
                  name="recurringRule"
                  rules={[{ required: true, message: "Select frequency" }]}
                  noStyle
                >
                  <Select
                    placeholder="Frequency"
                    style={{ width: 180 }}
                    options={[
                      { value: "Daily", label: "Daily" },
                      { value: "Weekly", label: "Weekly" },
                      { value: "Monthly", label: "Monthly" },
                    ]}
                  />
                </Form.Item>
                <Form.Item
                  name="recurringTime"
                  rules={[{ required: true, message: "Select time" }]}
                  noStyle
                >
                  <DatePicker showTime format="HH:mm" picker="time" />
                </Form.Item>
              </Space>
            </FieldRow>
          </>
        )}

        <FieldRow label="Budget Amount Entire Campaign" required>
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item
              name="Budget"
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  message: "Please input total campaign budget",
                },
                {
                  validator: async (_, value) => {
                    if (value === undefined || value === null || value === "") {
                      return Promise.resolve();
                    }
                    return Number(value) >= cost
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(`Minimum required budget: ${cost}`),
                        );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}
            >
              <InputNumber
                min={0}
                step={0.01}
                addonBefore="$"
                style={{ width: "100%" }}
                placeholder="Input Total Campaign Budget"
              />
            </Form.Item>
          </Space.Compact>
          <div style={{ color: "#8c8c8c", marginTop: 6 }}>
            Applies to this{" "}
            {scheduleType === TypeEnum.Onetime ? "one‑time" : "recurring"}{" "}
            campaign.
          </div>
        </FieldRow>

        <FieldRow label="Maximum Reach">
          <InputNumber
            style={{ width: "100%" }}
            value={maxReach}
            addonAfter="Members"
            formatter={(value) => {
              if (!value) return `0 / ${estimatedTotalMember}`;
              return `${Number(value)} / ${estimatedTotalMember}`;
            }}
            disabled
          />
          {maxReach > estimatedTotalMember ? (
            <Alert
              className="alert-info-budget-higher"
              type="info"
              showIcon
              style={{ padding: "8px 12px", marginTop: 10 }}
              description={
                <div style={{ fontSize: 14, lineHeight: 1.2 }}>
                  <div style={{ fontWeight: "bold", marginBottom: 4 }}>
                    Your budget is higher than required. Some budget may not be
                    used.
                  </div>
                  <div>
                    Adjust your budget or expand your targeting to utilize it
                    fully.
                  </div>
                </div>
              }
            />
          ) : maxReach === estimatedTotalMember ? (
            <div style={{ color: "#8c8c8c", marginTop: 10 }}>
              Recipients are selected from the top of the preview member list,
              up to the maximum number covered by your current budget.
            </div>
          ) : (
            <Alert
              style={{ marginTop: 10 }}
              description={
                <>
                  Only part of your target members will be reached based on your
                  budget.
                </>
              }
              type="warning"
              showIcon
            />
          )}
        </FieldRow>

        {/* <Row>
          <Col span={24}>
            <Alert
              message={<strong> Important Notice</strong>}
              description={
                <>
                  Estimation Total Members and Estimated Budget displayed are
                  estimates based on currently selected criteria. Actual member
                  count and budget used when the campaign runs may differ due to
                  real-time data changes, system conditions, or other factors.
                </>
              }
              type="warning"
              showIcon
            />
          </Col>
        </Row> */}

        {/* Message Content */}
        <Title level={5} style={{ marginBottom: 12 }}>
          Message Content
        </Title>

        <Divider />
        {SHOW_REWARD_TYPES.includes(selectedType) && (
          <FieldRow label="Rewards to Include" required>
            <Form.Item
              name="Voucher"
              rules={[
                {
                  validator: (_, value) => {
                    if (selectedVoucher || selectedEVoucher) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Please choose rewards to include"),
                    );
                  },
                },
              ]}
              style={{ width: "100%", marginBottom: 0 }}
            >
              {selectedVoucher || selectedEVoucher ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={summary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Rewards Selected:
                      </span>
                    }
                    prefix={
                      selectedVoucher || selectedEVoucher ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedVoucher || selectedEVoucher ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedKey(null);
                            setSelectedVoucher(null);
                            setSelectedEVoucher(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedVoucher ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setVoucherOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setVoucherOpen(true)}
                      type="primary"
                    >
                      Select Rewards
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary">No rewards selected</Text>
                  </Col>
                </Row>
              )}
            </Form.Item>
          </FieldRow>
        )}
        {(selectedCampaignChannel === ChannelEnum.WhatsAppStandard ||
          selectedCampaignChannel === ChannelEnum.WhatsAppPremium) && (
          <FieldRow label="Campaign Image (Optional)">
            <Form.Item name="Image" style={{ width: "100%", marginBottom: 0 }}>
              <Row gutter={[12, 12]}>
                <Col lg={4} xs={24}>
                  <Upload
                    accept="image/png,image/jpeg"
                    listType="picture"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    fileList={fileList}
                    maxCount={1}
                    className="w-100"
                    onChange={handleChangeUpload}
                  >
                    <Button
                      className="w-100"
                      disabled={previewUrl ? true : false}
                    >
                      Choose File
                    </Button>
                  </Upload>
                </Col>
                <Col lg={20} xs={24}>
                  {" "}
                  {fileList.length === 0 ? (
                    <Text type="secondary">
                      No file chosen <br />
                      Recommended Size: 1080×1080 (1:1) or 1920×1080 (16:9),
                      JPG/PNG, Max 5 MB.
                    </Text>
                  ) : isUploading ? (
                    <Progress
                      percent={progressUpload}
                      size="small"
                      status="active"
                    />
                  ) : (
                    <Space>
                      <Text>{fileList[0].name} </Text>
                      <Button
                        icon={<DeleteOutlined />}
                        type="text"
                        danger
                        onClick={handleRemove}
                      >
                        Remove
                      </Button>
                    </Space>
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: 10 }}>
                {previewUrl && (
                  <AntdImage
                    src={previewUrl}
                    alt="Campaign preview"
                    width={250}
                    height={150}
                    style={{
                      objectFit: "contain",
                      borderRadius: 8,
                      border: "1px solid #f0f0f0",
                    }}
                    preview={{ src: previewUrl }}
                  />
                )}
              </Row>
            </Form.Item>
          </FieldRow>
        )}

        {/* <FieldRow label="Message Template">
          <Form.Item name="messageTemplate" noStyle>
            <Select
              placeholder="Select Message Template"
              options={MessageTemplates}
              allowClear
              style={{ width: "100%" }}
            />
          </Form.Item>
        </FieldRow> */}

        <FieldRow label="Message Content" required>
          <Form.Item
            name="Message"
            style={{
              marginBottom: 0,
            }}
            rules={[
              {
                required: true,
                message: "Enter Message Content",
                validator: async (_, value) => {
                  // if (isFirstChange.current) {
                  //   isFirstChange.current = false;
                  //   return;
                  // }
                  const text = value?.replace(/<[^>]*>/g, "").trim();
                  return text
                    ? Promise.resolve()
                    : Promise.reject("Enter Message Content");
                },
              },
            ]}
          >
            {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
              // <Editor
              //   value={msg}
              //   onChange={(val) => {
              //     form.setFieldsValue({ Message: val });
              //     form.validateFields(["Message"]);
              //   }}
              // />
              <EditorQuill
                value={msg}
                onChange={(val) => {
                  if (isFirstChange.current) {
                    return;
                  }
                  if (val === "<p><br></p>")
                    form.setFieldsValue({ Message: val });
                  // form.validateFields(["Message"]);
                }}
              />
            )}
            {selectedCampaignChannel === ChannelEnum.WhatsAppPremium &&
              (selectedMessageTemplates ? (
                <Space.Compact style={{ width: "100%" }}>
                  <Input
                    readOnly
                    value={templatesSummary}
                    addonBefore={
                      <span style={{ color: "#8c8c8c" }}>
                        Template Selected:
                      </span>
                    }
                    prefix={
                      selectedMessageTemplates ? (
                        <CheckCircleFilled style={{ color: "#52c41a" }} />
                      ) : (
                        <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
                      )
                    }
                    suffix={
                      selectedMessageTemplates ? (
                        <Button
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => {
                            setSelectedMessageTemplates(null);
                          }}
                        />
                      ) : null
                    }
                    style={{
                      background: "#f5f5f5",
                      fontWeight: selectedMessageTemplates ? 600 : 400,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    type="primary"
                    onClick={() => setMessageTemplatesOpen(true)}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    Change
                  </Button>
                </Space.Compact>
              ) : (
                <Row gutter={[12, 12]}>
                  <Col lg={4} xs={24}>
                    <Button
                      className="w-100"
                      onClick={() => setMessageTemplatesOpen(true)}
                      type="primary"
                      disabled={!selectedType}
                    >
                      Select Template
                    </Button>
                  </Col>
                  <Col
                    lg={20}
                    xs={24}
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Text type="secondary">No template selected</Text>
                  </Col>
                </Row>
              ))}

            {selectedCampaignChannel === ChannelEnum.Sms && (
              <Input.TextArea
                rows={4}
                placeholder="Enter Message Content"
                autoSize
              />
            )}
          </Form.Item>
        </FieldRow>
        {selectedCampaignChannel !== ChannelEnum.WhatsAppPremium && (
          <FieldRow
            label={
              <span>
                Message Variables{" "}
                <Tooltip
                  title={
                    <>
                      Personalize your message with variables.
                      <br />
                      These will be replaced with real data when the campaign is
                      sent
                    </>
                  }
                  placement="right"
                >
                  <InfoCircleOutlined
                    style={{ color: "#8c8c8c", cursor: "pointer" }}
                  />
                </Tooltip>
              </span>
            }
          >
            {" "}
            <div style={{ marginTop: 8 }}>
              <div style={{ marginTop: 8 }}>
                <Space size={[8, 8]} wrap>
                  {filteredVars.map((v) => (
                    <Tag
                      key={v}
                      style={{ cursor: "pointer" }}
                      onClick={() => insertVar(v)}
                    >
                      {v}
                    </Tag>
                  ))}
                </Space>
              </div>
            </div>
          </FieldRow>
        )}
        {showLink &&
          link &&
          selectedCampaignChannel !== ChannelEnum.WhatsAppStandard && (
            <FieldRow label={<span>Campaign Tracking Link (CTA)</span>}>
              <Form.Item
                name="Link"
                label=""
                rules={[
                  { required: false },
                  { type: "url", message: "Please enter a valid URL" },
                ]}
              >
                <Input
                  prefix={<LinkOutlined />}
                  placeholder="Enter a Call to Action Link"
                  value={link}
                  disabled={true}
                />
                <div style={{ color: "#8c8c8c", marginTop: 6 }}>
                  This link is automatically generated for your campaign
                  tracking. Allowing you to monitor customer engagement and
                  clicks.{" "}
                </div>
              </Form.Item>
            </FieldRow>
          )}

        <FieldRow
          label={
            <span>
              Preview Message{" "}
              <Tooltip
                title={
                  <>
                    This preview message shows the content message
                    <br /> along with the Campaign Tracking Link (CTA),
                    <br /> While the Unsubscribe link displayed is only a
                    placeholder.
                  </>
                }
                placement="right"
              >
                <InfoCircleOutlined
                  style={{ color: "#8c8c8c", cursor: "pointer" }}
                />
              </Tooltip>
            </span>
          }
        >
          {/* Preview */}
          <div>
            <Text strong>Live Preview</Text>
            <Card size="small" style={{ marginTop: 8 }}>
              <div style={{ color: "#8c8c8c" }}>
                {previewUrl && (
                  <>
                    <AntdImage
                      src={previewUrl}
                      alt="Campaign preview"
                      width={250}
                      height={150}
                      style={{
                        objectFit: "contain",
                        borderRadius: 8,
                        border: "1px solid #f0f0f0",
                      }}
                      preview={false}
                    />
                    <br />
                  </>
                )}
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: finalMessage
                        ? DOMPurify.sanitize(
                            finalMessage.replace(/\n/g, "<br/>"),
                          )
                        : "Your message will appear here...",
                    }}
                  />
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppPremium && (
                  <>
                    <div style={{ fontSize: 14 }}>
                      {" "}
                      {selectedMessageTemplates
                        ? renderMessage(selectedMessageTemplates.Content)
                        : "Your message templates will appear here..."}
                    </div>

                    <Space
                      direction="vertical"
                      style={{ width: "100%", marginTop: 16 }}
                    >
                      {selectedMessageTemplates?.TextLink1 && (
                        <Button
                          block
                          style={{
                            color: "#00A884",
                            fontWeight: 600,
                          }}
                          icon={
                            <img
                              src="/icon/icon-button-whatsapp.svg"
                              style={{ width: 16 }}
                            />
                          }
                        >
                          {selectedMessageTemplates?.TextLink1}
                        </Button>
                      )}

                      {selectedMessageTemplates?.TextLink2 && (
                        <Button
                          block
                          style={{
                            color: "#00A884",
                            fontWeight: 600,
                          }}
                          icon={
                            <img
                              src="/icon/icon-button-whatsapp.svg"
                              style={{ width: 16 }}
                            />
                          }
                        >
                          {selectedMessageTemplates?.TextLink2}
                        </Button>
                      )}
                    </Space>
                  </>
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: !isEmptyHtml(msg)
                        ? DOMPurify.sanitize(msg!)
                        : "Your Message will appear here...",
                    }}
                  />
                )}
                {selectedCampaignChannel === ChannelEnum.WhatsAppStandard && (
                  <>
                    <br />
                    👉 Reply with{" "}
                    <b>OK to receive your membership login link</b>
                    <br />
                    👉 Reply with{" "}
                    <b>STOP to opt out of future WhatsApp messages</b>
                  </>
                )}{" "}
              </div>
            </Card>
            <Row justify="space-between" style={{ marginTop: 6 }}>
              <Col>
                <Text type="secondary">
                  {selectedCampaignChannel === ChannelEnum.Sms && (
                    <Text type="secondary">Characters used: {charCount}</Text>
                  )}
                  {/* ( {MAX_LENGTH} per SMS ) */}
                </Text>
              </Col>
              <Col>
                {selectedCampaignChannel === ChannelEnum.Sms && (
                  <Text type="secondary">Total SMS: {smsSegments}</Text>
                )}
              </Col>
            </Row>
          </div>
        </FieldRow>
        {selectedCampaignChannel === ChannelEnum.Sms && (
          <Alert
            message={<strong> Important Notice</strong>}
            description={
              <>
                Some messages may use extra SMS when variables are applied. This
                can affect total recipients covered by your budget.
              </>
            }
            type="warning"
            showIcon
          />
        )}

        {/* Bottom actions */}
        {!isMobileOrTablet ? (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({ ...values, Status: StatusEnum.Draft });
                  } catch (error) {
                    handleError(error);
                  }
                }}
              >
                Save as Draft
              </Button>
            </Col>
            <Col>
              <Button
                loading={loading || loadingRefreshMember}
                type="primary"
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({
                      ...values,
                      Status: StatusEnum.Active,
                    });
                  } catch (error) {
                    handleError(error);
                  }
                }}
              >
                Submit
              </Button>
            </Col>
          </Row>
        ) : (
          <Row justify="end" gutter={8} style={{ marginTop: 16 }}>
            <Col xs={12}>
              <Button
                className="w-100"
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  const values = await form.validateFields();
                  await handleSubmit({ ...values, Status: StatusEnum.Draft });
                }}
              >
                Save as Draft
              </Button>
            </Col>
            <Col xs={12}>
              <Button
                className="w-100"
                type="primary"
                loading={loading || loadingRefreshMember}
                onClick={async () => {
                  try {
                    const values = await form.validateFields();
                    await handleSubmit({
                      ...values,
                      Status: StatusEnum.Active,
                    });
                  } catch (error) {
                    handleError(error);
                  }
                }}
              >
                Send Now
              </Button>
            </Col>
          </Row>
        )}
      </Card>
      <Modal
        title={
          selectedType === CampaignTypeEnum.EVoucher
            ? "Select E-Voucher(s)"
            : "Select Voucher(s)"
        }
        open={voucherOpen}
        onCancel={() => {
          setSelectedKey(null);
          setSelectedVoucher(null);
          setVoucherOpen(false);
          setSelectedEVoucher(null);
        }}
        footer={null}
        maskClosable={false}
        width={1200}
      >
        <Row justify={"space-between"}>
          <Col span={12}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder={`Search by ${
                selectedType === CampaignTypeEnum.EVoucher ? "E-" : ""
              }Voucher's name`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Col>
          <Col span={12} style={{ textAlign: "end" }}>
            <a
              href={`${
                selectedType === CampaignTypeEnum.EVoucher
                  ? process.env.REACT_APP_DASHBOARD_GETZPASS_URL
                  : process.env.REACT_APP_DASHBOARD_URL
              }${
                selectedType === CampaignTypeEnum.EVoucher
                  ? "/Product#/e-voucher/create/0"
                  : "/voucher/create"
              }`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button type="primary">
                {" "}
                <PlusOutlined />{" "}
                {selectedType === CampaignTypeEnum.EVoucher
                  ? "Create E-Voucher"
                  : "Create Voucher"}
              </Button>
            </a>
          </Col>
        </Row>

        <Space direction="vertical" style={{ width: "100%" }}>
          <VoucherTable
            selectedType={selectedType}
            voucherData={filteredVouchers}
            eVoucherData={filteredVEouchers}
            selectedKey={selectedKey}
            onChangeSelected={(key, row) => {
              setSelectedKey(key);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
            }}
            onAddSelect={(row) => {
              setSelectedKey(row.Id);
              if (
                selectedType === CampaignTypeEnum.Voucher ||
                selectedType === CampaignTypeEnum.VoucherPoint
              ) {
                setSelectedVoucher(row);
              }
              if (selectedType === CampaignTypeEnum.EVoucher) {
                setSelectedEVoucher(row);
              }
              setVoucherOpen(false);
            }}
            loading={loadingVoucher}
          />
        </Space>
      </Modal>
      {/* Billing Modal */}
      <BillingModal
        open={isBillingModalOpen}
        campaignName={campaignName}
        channel={selectedCampaignChannel}
        estimatedMembers={estimatedTotalMember}
        costPerMessage={cost}
        smsSegments={smsSegments}
        maxBudget={pendingPayload?.Budget}
        mode={pendingPayload?.Mode}
        campaignStart={pendingPayload?.CampaignStart}
        campaignEnd={pendingPayload?.CampaignEnd}
        confirmLabel="Submit Campaign"
        onClose={() => setIsBillingModalOpen(false)}
        onPaymentLater={onPaymentlater}
        onConfirmPayment={async (_email, referenceCode) => {
          try {
            setLoading(true);
            const resultId = await campaignService.createCampaignDetails({
              ...pendingPayload!,
              IsBilled: 1,
              ReferenceCode: referenceCode,
            });
            if (!resultId) throw new Error("Failed to create campaign");
            setNewCampaignId(resultId);
          } catch {
            console.error(
              "Error try catch when create campaign with confirm payment",
            );
          } finally {
            setLoading(false);
          }
        }}
        onSuccess={() => {
          setIsBillingModalOpen(false);
          setIsPaymentSuccessModalOpen(true);
        }}
        onFailed={() => {
          setIsBillingModalOpen(false);
          setIsPaymentFailedModalOpen(true);
        }}
      />

      {/* Payment Success Modal */}
      <Modal
        open={isPaymentSuccessModalOpen}
        onCancel={() => setIsPaymentSuccessModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <CheckCircleFilled style={{ fontSize: 80, color: "#2ecc71" }} />
          <Title level={4} style={{ margin: 0 }}>
            Campaign Created Successfully
          </Title>
          <Text type="secondary">
            Your campaign <b>{campaignName}</b> has been created and payment has
            been processed successfully.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Button
          type="primary"
          size="large"
          className="w-100"
          onClick={() => {
            setIsPaymentSuccessModalOpen(false);
            navigate(
              `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${newCampaignId}`,
            );
          }}
          style={{ marginTop: 8 }}
        >
          View Details
        </Button>
      </Modal>

      {/* Payment Failed Modal */}
      <Modal
        open={isPaymentFailedModalOpen}
        onCancel={() => setIsPaymentFailedModalOpen(false)}
        footer={null}
        centered
        width={520}
        closable={false}
        maskClosable={false}
        styles={{ body: { paddingTop: 28, paddingBottom: 24 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%", textAlign: "center" }}
          size={16}
        >
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#ff4d4f" }} />
          <Title level={4} style={{ margin: 0 }}>
            Payment Failed
          </Title>
          <Text type="secondary">
            Your campaign <b>{campaignName}</b> was created but payment could
            not be processed. You can retry payment from the campaign detail
            page.
          </Text>
        </Space>
        <Divider style={{ margin: "16px 0 8px" }} />
        <Row gutter={[12, 12]}>
          <Col span={12}>
            <Button
              size="large"
              className="w-100"
              onClick={() => {
                setIsPaymentFailedModalOpen(false);
                onPaymentlater();
              }}
              style={{ marginTop: 8 }}
            >
              Set Up Payment Later
            </Button>
          </Col>
          <Col span={12}>
            <Button
              type="primary"
              size="large"
              className="w-100"
              onClick={() => {
                setIsPaymentFailedModalOpen(false);
                setIsBillingModalOpen(true);
              }}
              // navigate(
              //   `${RoutePaths.IFRAME_DETAIL_CAMPAIGN}${location.search}&id=${newCampaignId}`,
              // );
              style={{ marginTop: 8 }}
            >
              Retry Payment
            </Button>
          </Col>
        </Row>
      </Modal>

      <Modal
        open={isModalWarningCreateCampaignVisible}
        onCancel={() => setIsModalWarningCreateCampaignVisible(false)}
        footer={null}
        centered
        width={620}
        closeIcon={<CloseOutlined />}
        styles={{ body: { paddingTop: 28, paddingBottom: 16 } }}
      >
        <Space
          direction="vertical"
          align="center"
          style={{ width: "100%" }}
          size={16}
        >
          <ExclamationCircleFilled style={{ fontSize: 80, color: "#EEB711" }} />
          <Title level={4} style={{ margin: 0, textAlign: "center" }}>
            Send with Partial Reach
          </Title>
          <Text type="secondary" style={{ textAlign: "center" }}>
            Your budget will only cover <b>{maxReach}</b> of{" "}
            <b>{estimatedTotalMember}</b> targeted members. Do you want to
            continue?
          </Text>

          <Space style={{ marginTop: 12 }}>
            <Button
              size="large"
              onClick={() => setIsModalWarningCreateCampaignVisible(false)}
            >
              Go Back
            </Button>
            <Button
              type="primary"
              size="large"
              // onClick={() => handleSaveCampaign()}
            >
              Send Anyway
            </Button>
          </Space>
        </Space>
      </Modal>
      <MessageTemplatesModal
        campaignType={selectedType}
        open={messageTemplatesOpen}
        templates={listMessageTemplates}
        onCancel={() => setMessageTemplatesOpen(false)}
        onSelect={(template) => {
          setSelectedMessageTemplates(template);
          setMessageTemplatesOpen(false);
        }}
      />
      <RFMGeneratingModal
        visible={loading || loadingRefreshMember}
        message="Preparing Campaign Data..."
      />
      <PreviewMemberModal
        open={isPreviewOpen}
        onClose={closePreview}
        estimatedMember={estimatedMember}
      />
    </Form>
  );
};

export default CreateCampaign;
